import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { lastValueFrom } from 'rxjs';

import { GBE_BASE_URL } from '@core/tokens/gbe-url.token';

import {
  GeoLocation,
  ProfileType
} from '@features/components/visualization/mapbox/models/map-data';
import { FlexiblePolylineDecoder } from '@features/components/visualization/mapbox/utils/flexible-polyline-decoder';

/**
 * The vehicle geoprofile carries eight transport modes; GBE's provider surface carries two.
 * The mapping is therefore coarse, and stated here rather than implied at the call site.
 */
export function transportModeToProfile(mode: string | null | undefined): ProfileType {
  return mode === 'Truck' || mode === 'Bus' || mode === 'PrivateBus'
    ? ProfileType.TRUCK
    : ProfileType.CAR;
}

/**
 * GBE reads each pair longitude first — its OSRM and Mapbox providers hand the parameter
 * straight to APIs in that order, and its HERE provider flips the pair itself. A latitude-first
 * pair is not rejected, it is silently routed from somewhere else on the globe.
 */
export function buildCoordinatesParam(points: GeoLocation[]): string {
  return points.map(({ lat, lng }) => `${lng},${lat}`).join(';');
}

export function decodeLegs(legs: string[] | null | undefined): GeoLocation[] {
  return (legs ?? []).flatMap((leg) =>
    FlexiblePolylineDecoder.decode(leg).map(({ lat, lng }) => ({ lat, lng }))
  );
}

@Injectable({ providedIn: 'root' })
export class GeoArcRouteService {
  private readonly gbeUrl = inject(GBE_BASE_URL);
  private readonly http = inject(HttpClient);

  /**
   * One request through the ordered points. GBE returns one encoded flexible polyline per
   * leg; the legs are concatenated into a single path.
   */
  async computePath(points: GeoLocation[], profile: ProfileType): Promise<GeoLocation[]> {
    if (points.length < 2) {
      return [];
    }
    const coordinates = buildCoordinatesParam(points);
    const url = `${this.gbeUrl}/routes/${coordinates}?profileType=${profile}`;
    const legs = await lastValueFrom(this.http.get<string[]>(url));
    return decodeLegs(legs);
  }
}
